import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import * as Http from '../../../http';
import { t, onLangChange } from '../../../i18n';
import Loading from '../../../components/Loading';
import ResultTable from '../CapMrCor/ResultTable';
import RarocCrResultTable from '../CrResultTable';
import '../CapMrCor/cap-mr-cor.style.scss';
import '../CapMrCor/raroc-theme.style.scss';

const fmtDate = (d) => (d ? String(d).replace('T', ' ').slice(0, 19) : '—');
const day = (d) => String(d || '').slice(0, 10);
const COMPONENT_CAP_MR_COR = 'cap-mr-cor';
const COMPONENT_RAROC = 'raroc';
const COMPONENT_RAROC_CR = 'raroc-cr';
const CAP_PREFIX = 'STG_MR_COR_CAP_';
const CAP_PREFIX_LEGACY = 'STG_MR & COR CAP_';
const CR_PREFIX = 'RAROC_CR_';
const DATASET_PREFIX = 'RAROC_';

const ensureDatasetPrefix = (raw = '') => {
    const clean = String(raw || '').trim();
    if (!clean) return '';
    if (clean.toUpperCase().startsWith(DATASET_PREFIX)) return clean;
    return `${DATASET_PREFIX}${clean}`;
};

const normalizeItems = (rows = [], component) =>
    rows.map((row) => ({
        ...row,
        component: row.component || component,
        has_result: typeof row.has_result === 'boolean' ? row.has_result : String(row.status || '').toUpperCase() === 'CALCULATED',
    }));

const isRarocCrItem = (row = {}) => {
    const name = String(row.file_name || '').toUpperCase();
    const comp = String(row.component || '').toLowerCase();
    return comp === COMPONENT_RAROC_CR || name.startsWith('RAROC_CR_');
};

const datasetKey = (fileName = '') => {
    if (fileName.startsWith(CAP_PREFIX)) return ensureDatasetPrefix(fileName.slice(CAP_PREFIX.length).replace(/^RAROC_/i, ''));
    if (fileName.startsWith(CAP_PREFIX_LEGACY)) return ensureDatasetPrefix(fileName.slice(CAP_PREFIX_LEGACY.length).replace(/^RAROC_/i, ''));
    if (fileName.startsWith(CR_PREFIX)) return ensureDatasetPrefix(fileName.slice(CR_PREFIX.length).replace(/^RAROC_/i, ''));
    return ensureDatasetPrefix(String(fileName || '').replace(/^RAROC_/i, ''));
};

const mergeByDataset = (capItems = [], crItems = []) => {
    const grouped = {};
    [...capItems, ...crItems].forEach((row) => {
        const key = `${day(row.report_date)}__${datasetKey(row.file_name || '')}`;
        if (!grouped[key]) {
            grouped[key] = {
                key,
                report_date: row.report_date,
                dataset: datasetKey(row.file_name || ''),
                upload_time: row.upload_time,
                approval_time: row.approval_time,
                upload_by: row.upload_by,
                approval_by: row.approval_by,
                status: row.status,
                has_result: !!row.has_result,
                capItem: null,
                crItem: null,
            };
        }
        if (row.component === COMPONENT_RAROC_CR) grouped[key].crItem = row;
        else grouped[key].capItem = row;

        grouped[key].has_result = grouped[key].has_result || !!row.has_result;
        if (new Date(row.upload_time || 0).getTime() > new Date(grouped[key].upload_time || 0).getTime()) {
            grouped[key].upload_time = row.upload_time;
            grouped[key].upload_by = row.upload_by;
        }
    });
    return Object.values(grouped).sort((a, b) => new Date(b.upload_time || 0).getTime() - new Date(a.upload_time || 0).getTime());
};

// STG_MR & COR CAP — File Management
export default class FileManagement extends Component {
    state = {
        items: [],
        loading: true,
        viewing: null,
        viewResult: null,
        viewingComponent: null,
        searchField: 'report_name',
        query: '',
        applied: { field: 'report_name', q: '' },
        _lang: null,
    };

    componentDidMount() {
        this._unsub = onLangChange(() => this.setState({ _lang: Date.now() }));
        this.load();
    }
    componentWillUnmount() {
        this._unsub && this._unsub();
    }

    load = () => {
        this.setState({ loading: true });
        Promise.all([Http.getCapMrCorList(), Http.getUploadManagement(COMPONENT_RAROC)])
            .then(([capRes, crRes]) => {
                const capItems = capRes && capRes.Success ? normalizeItems(capRes.Data || [], COMPONENT_CAP_MR_COR) : [];
                const crItems =
                    crRes && crRes.Success
                        ? normalizeItems((crRes.Data || []).filter(isRarocCrItem), COMPONENT_RAROC_CR).map((row) => ({
                              ...row,
                              component: COMPONENT_RAROC_CR,
                          }))
                        : [];

                const items = mergeByDataset(capItems, crItems);
                this.setState({ items, loading: false });
            })
            .catch(() => this.setState({ loading: false }));
    };

    onSearch = () => this.setState({ applied: { field: this.state.searchField, q: this.state.query.trim().toLowerCase() } });

    onView = (item) => {
        if (!item.has_result) return;
        this.setState({ loading: true });
        // RAROC là MỘT dataset: CAP + CR chung report_name nên kết quả nằm cùng 1 dòng.
        // Với dữ liệu đã đồng nhất, crItem có thể null → fallback sang capItem để vẫn lấy được CR.
        const capSrc = item.capItem || item.crItem || null;
        const crSrc = item.crItem || item.capItem || null;
        const reqs = [capSrc ? Http.getCapMrCorResult(day(capSrc.report_date), capSrc.file_name) : Promise.resolve(null), crSrc ? Http.getRarocCrResult(day(crSrc.report_date), crSrc.file_name) : Promise.resolve(null)];

        Promise.all(reqs)
            .then(([capRes, crRes]) => {
                const capRec = capRes && capRes.Success && capRes.Data && capRes.Data[0];
                const crRec = crRes && crRes.Success && crRes.Data && crRes.Data[0];
                const hasAny = !!(capRec || crRec);
                this.setState({
                    loading: false,
                    viewing: item.dataset,
                    viewResult: hasAny ? { dataset: item.dataset } : null,
                    viewResultCap: capRec ? capRec.data : null,
                    viewResultCr: crRec ? crRec.data : null,
                });
                if (hasAny) setTimeout(() => this._view && this._view.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
            })
            .catch(() => this.setState({ loading: false }));
    };

    onDelete = (item) => {
        if (!window.confirm(t('rstg.fm.confirmPre') + item.dataset + t('rstg.fm.confirmPost'))) return;
        this.setState({ loading: true });
        const reqs = [];
        if (item.capItem) reqs.push(Http.deleteCapMrCor(day(item.capItem.report_date), item.capItem.file_name));
        if (item.crItem) {
            reqs.push(
                Http.deleteListReport(COMPONENT_RAROC, {
                    report_date: day(item.crItem.report_date),
                    file_name: item.crItem.file_name,
                }),
            );
        }

        Promise.all(reqs)
            .then(() => {
                this.setState({ viewing: null, viewResult: null, viewResultCap: null, viewResultCr: null });
                this.load();
            })
            .catch(() => this.setState({ loading: false }));
    };

    filtered() {
        const { items, applied } = this.state;
        if (!applied.q) return items;
        return items.filter((it) => {
            const val = applied.field === 'report_date' ? day(it.report_date) : String(it.dataset || it.file_name || '');
            return val.toLowerCase().includes(applied.q);
        });
    }

    render() {
        const { loading, viewing, viewResult, viewResultCap, viewResultCr, searchField, query } = this.state;
        const rows = this.filtered();

        return (
            <div className='content'>
                <div className='container-fluid rstg'>
                    {loading && <Loading />}

                    <div className='rstg-title'>{t('rstg.fm.title')}</div>

                    <div className='rstg-card'>
                        {/* filter */}
                        <div className='rstg-filter'>
                            <select className='rstg-select' value={searchField} onChange={(e) => this.setState({ searchField: e.target.value })}>
                                <option value='report_name'>{t('rstg.col.reportName')}</option>
                                <option value='report_date'>{t('rstg.col.reportDate')}</option>
                            </select>
                            <input className='rstg-search-input' value={query} placeholder='' onChange={(e) => this.setState({ query: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && this.onSearch()} />
                            <button className='rstg-search-btn' onClick={this.onSearch}>
                                {t('rstg.btn.search')}
                            </button>
                        </div>

                        <div className='rstg-table-wrap'>
                            <table className='rstg-table'>
                                <thead>
                                    <tr>
                                        <th>{t('rstg.col.reportDate')}</th>
                                        <th>{t('rstg.col.reportName')}</th>
                                        <th>{t('rstg.col.action')}</th>
                                        <th>{t('rstg.col.uploader')}</th>
                                        <th>{t('rstg.col.uploadTime')}</th>
                                        <th>{t('rstg.col.approver')}</th>
                                        <th>{t('rstg.col.approvalTime')}</th>
                                        <th>{t('rstg.col.status')}</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 && (
                                        <tr>
                                            <td colSpan='9'>
                                                <div className='rstg-empty'>{t('rstg.fm.empty')}</div>
                                            </td>
                                        </tr>
                                    )}
                                    {rows.map((it) => (
                                        <tr key={it.key}>
                                            <td>{day(it.report_date)}</td>
                                            <td>
                                                <Link
                                                    className='rstg-link'
                                                    to={{
                                                        pathname: `/raroc/file-info/${day(it.report_date)}/${encodeURIComponent(it.dataset)}`,
                                                        state: it,
                                                    }}
                                                >
                                                    {it.dataset}
                                                </Link>
                                            </td>
                                            <td>{t('rstg.fm.uploadOnly')}</td>
                                            <td>{it.upload_by}</td>
                                            <td>{fmtDate(it.upload_time)}</td>
                                            <td>{it.approval_by || '—'}</td>
                                            <td>{fmtDate(it.approval_time)}</td>
                                            <td>{it.status}</td>
                                            <td>
                                                <i className='pe-7s-trash rstg-del' title={t('rstg.btn.delete')} onClick={() => this.onDelete(it)} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {viewResult && (
                        <div className='rstg-card' ref={(el) => (this._view = el)}>
                            <div className='rstg-filter' style={{ justifyContent: 'space-between' }}>
                                <div className='rstg-section-title' style={{ margin: 0 }}>
                                    {t('rstg.fm.resultTitle')}: {viewing}
                                </div>
                                <button className='rstg-search-btn' style={{ background: '#eef0f4', color: '#3a4252' }} onClick={() => this.setState({ viewResult: null, viewResultCap: null, viewResultCr: null, viewing: null })}>
                                    {t('rstg.btn.close')}
                                </button>
                            </div>
                            {viewResultCap && (
                                <div>
                                    <div className='rstg-section-title'>STG_MR & COR CAP</div>
                                    <ResultTable result={viewResultCap} />
                                </div>
                            )}
                            {viewResultCr && (
                                <div style={{ marginTop: 12 }}>
                                    <div className='rstg-section-title'>RAROC CR</div>
                                    <RarocCrResultTable result={viewResultCr} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// src/pages/Raroc/FileManagement/FileManagement.js